import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { Button, ListGroupItem } from 'reactstrap';
import { TypedMemo } from '../common/types';
import { teamPlanLabel } from '../team/teamAcceptMessages';
import { workspaceMessages } from './workspaceMessages';
import { WorkspaceMark } from './WorkspaceMark';
import type { WorkspaceSummary } from './useWorkspaces';
import styles from './YourWorkspacesRow.module.scss';

interface YourWorkspacesRowProps {
  workspace: WorkspaceSummary;
  /** A switch or a leave runs: every button is off. */
  isBusy: boolean;
  onSwitch: (workspace: WorkspaceSummary) => void;
  onLeave: (workspace: WorkspaceSummary) => void;
}

/**
 * design: workspaces d1 / e1 / e3 "Your workspaces" row. Switch on every workspace that is not
 * open. Leave only on a member workspace that is not the home one: nobody leaves their home, and
 * an old-model member (D8) has a MEMBER row on it (e3). The open home row says why it has no Leave.
 */
const YourWorkspacesRowComponent: React.FC<YourWorkspacesRowProps> = ({ workspace, isBusy, onSwitch, onLeave }) => {
  const intl = useIntl();
  const isOwner = workspace.role === 'OWNER';
  const canLeave = !isOwner && !workspace.isHome;
  const details = intl.formatMessage(workspaceMessages.planArtists, {
    plan: teamPlanLabel(intl, workspace.plan),
    count: workspace.artistCount,
  });
  const meta = isOwner
    ? intl.formatMessage({ id: 'app.sml.workspaces.list.ownerMeta', defaultMessage: 'Owner · {details}' }, { details })
    : intl.formatMessage(
        { id: 'app.sml.workspaces.list.memberMeta', defaultMessage: 'Member · {email} · {details}' },
        { email: workspace.ownerEmail, details }
      );
  return (
    <ListGroupItem tag="li" className="d-flex align-items-center gap-3 px-0">
      <WorkspaceMark licenseId={workspace.licenseId} name={workspace.name} size="large" />
      <div className={clsx('flex-grow-1', styles.text)}>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <span className={styles.name}>{workspace.name}</span>
          {workspace.isHome && (
            <span className={styles.homeChip}>{intl.formatMessage(workspaceMessages.yourWorkspace)}</span>
          )}
          {workspace.isActive && (
            <span className={styles.current}>
              {intl.formatMessage({ id: 'app.sml.workspaces.list.openNow', defaultMessage: 'Open now' })}
            </span>
          )}
        </div>
        <div className={styles.meta} data-clarity-mask={isOwner ? undefined : 'true'}>
          {meta}
        </div>
      </div>
      <div className="d-flex align-items-center gap-3 text-nowrap">
        {!workspace.isActive && (
          <Button color="link" size="sm" className="p-0" disabled={isBusy} onClick={() => onSwitch(workspace)}>
            {intl.formatMessage({ id: 'app.sml.workspaces.list.switch', defaultMessage: 'Switch' })}
          </Button>
        )}
        {canLeave && (
          <Button color="danger" outline size="sm" disabled={isBusy} onClick={() => onLeave(workspace)}>
            {intl.formatMessage({ id: 'app.sml.workspaces.list.leave', defaultMessage: 'Leave' })}
          </Button>
        )}
        {workspace.isHome && workspace.isActive && (
          <small className="text-muted">
            {isOwner
              ? intl.formatMessage({
                  id: 'app.sml.workspaces.list.cannotLeaveOwn',
                  defaultMessage: 'You cannot leave your own workspace',
                })
              : intl.formatMessage({
                  id: 'app.sml.workspaces.list.ownerCanRemove',
                  defaultMessage: 'The owner can remove you',
                })}
          </small>
        )}
      </div>
    </ListGroupItem>
  );
};

export const YourWorkspacesRow = TypedMemo(YourWorkspacesRowComponent);
